<script>
    /*
    	VerticalCategoryPicker.svelte

        This component is used for choosing sub-categories for a filtered search.

        Contains the following components:

        <ResourcePicker> 
            
            <SelectedCategories>
                <SelectedCategoryButton>
                <SelectedCategoryButton>
                <SelectedCategoryButton>
                <SelectedCategoryButton>

            <AvailableCategories>
                <AvailableCategoryButton>
                <AvailableCategoryButton>
                <AvailableCategoryButton>
                <AvailableCategoryButton>

    */

    import { onMount } from 'svelte';
    import { level0Node, level1Node, level2Node, level3Node, level4Node } from '../../stores/ResourceMenuStore.js';
    import { currentMenuLevel, currentMenuNode } from '../../stores/ResourceMenuStore.js';
    import {asyncValidatePostalCode, validatePostalCode} from "../../common/utils.js";
    import { setWarningAlert } from '../../stores/AlertsStore';
    import { hasServiceIds, hasChildrenWithServiceIds } from '../../common/public-search-utils.js';

    // TCARE components
    import VerticalSelectedCategories from './VerticalSelectedCategories.svelte';
    import VerticalAvailableCategories from './VerticalAvailableCategories.svelte';
    import {isShowingZipError} from "../../stores/ScreenSettingsStore";
    import {masterSearch} from "../../common/master-objects";

    // SMUI components

    export let searchStore;
    export let onSearch;

    $: isShowingAvailCats = true;
    $: childCategoriesWithServices = getChildCategoriesWithServices($currentMenuNode);

    const NO_POSTAL_WARNING_MSG = 'Please enter a valid ZIP Code.';

    onMount(() => {
    })

    const getChildCategoriesWithServices = (node) => {
        const children = [];
        if (!node) return [];
        if (!node.children) return [];
        for (const child of node.children) {
            if (hasServiceIds(child)) {
                child.menuLevel = $currentMenuLevel + 1;                        // new 11/17 for ResourcePicker
                children.push(child);
            }
        }
        return children;
    }

    // removes the button from "Selected" and goes to correct menu level
    const onSelectedCatButtonClick = async (node) => {
        const isValidZipCode = await asyncValidatePostalCode($masterSearch.location.postalCode)
        if (!isValidZipCode) {
            $isShowingZipError = true
            return false
        }

        node.isSelected = false;
        $currentMenuLevel = node.menuLevel - 1;             // new prop 11/07

        if ($currentMenuLevel === 3) {
            if ($level4Node !== null) {
                $level4Node.isSelected = false
            }
            $level4Node = null;
            $currentMenuNode = $level3Node;
        }
        if ($currentMenuLevel === 2) {
            if ($level4Node !== null) {
                $level4Node.isSelected = false
            }
            $level4Node = null;
            if ($level3Node !== null) {
                $level3Node.isSelected = false
            }
            $level3Node = null;
            $currentMenuNode = $level2Node;
        }
        if ($currentMenuLevel === 1) {
            if ($level4Node !== null) {
                $level4Node.isSelected = false
            }
            $level4Node = null;
            if ($level3Node !== null) {
                $level3Node.isSelected = false
            }
            $level3Node = null;
            if ($level2Node !== null) {
                $level2Node.isSelected = false
            }
            $level2Node = null;
            $currentMenuNode = $level1Node;
        }

        // still a main category selected in menu
        if ($currentMenuLevel === 0) {
            if ($level4Node !== null) {
                $level4Node.isSelected = false
            }
            $level4Node = null;
            if ($level3Node !== null) {
                $level3Node.isSelected = false
            }
            $level3Node = null;
            if ($level2Node !== null) {
                $level2Node.isSelected = false
            }
            $level2Node = null;
            if ($level1Node !== null) {
                $level1Node.isSelected = false
            }
            $level1Node = null;
            $currentMenuNode = $level0Node;
        }

        // no categories selected - run postal search
        if ($currentMenuLevel < 0) {
            $level4Node = null;
            $level3Node = null;
            $level2Node = null;
            $level1Node = null;
            $level0Node = null;
            $currentMenuNode = null;
        }
        onSearch();         // run the ResourceExplorerPage filter search
    }
    // levels can only go up here
    // run the filterSearch if valid ZIP Code
    const onAvailableCatButtonClick = async (node) => {
        const isValidZipCode = await asyncValidatePostalCode($masterSearch.location.postalCode)
        if (!isValidZipCode) {
            $isShowingZipError = true
            return false
        }

        node.isSelected = true;
        $currentMenuNode = node;
        $currentMenuLevel = $currentMenuLevel + 1;
        // should never be able to go past 4 - no buttons visible
        if ($currentMenuLevel > 4) $currentMenuLevel = 4;

        if ($currentMenuLevel === 1) {
            $level1Node = node;
            $level2Node = null;
            $level3Node = null;
            $level4Node = null;
        }
        if ($currentMenuLevel === 2) {
            $level2Node = node;
            $level3Node = null;
            $level4Node = null;
        }
        if ($currentMenuLevel === 3) {
            $level3Node = node;
            $level4Node = null;
        }
        if ($currentMenuLevel === 4) {
            $level4Node = node;
        }

        onSearch();         // run the ResourceExplorerPage filter search
    }

</script>


<style>
    .resource-picker {
        /* height: 50px; */
        width: 255px;
        margin-top: 20px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        color: var(--tcui-primary);
        font-size: 15px;
        /* border: 1px solid red; */
    }

    .specify-category {
        margin: 10px 0px;
        font-size: 13px;
        /* font-weight: 600; */
        color: var(--tcui-secondary);
        text-transform: uppercase;
    }

    @media screen and (max-width: 600px) {
    }

    @media screen and (min-width: 601px) and (max-width: 1300px) {
    }

    @media screen and (min-width: 1201px) {
    }

</style>

<div class="resource-picker">
    {#if $level0Node}
        <VerticalSelectedCategories {searchStore} {onSelectedCatButtonClick} />

        <!-- need to check hasChildServices() later -->
        {#if childCategoriesWithServices.length > 0}
            <div class="specify-category">Specify service within {$currentMenuNode.label}</div>
            {#if isShowingAvailCats}
                <VerticalAvailableCategories {onAvailableCatButtonClick} />
            {/if}
        {/if}

    {/if}
</div>

